import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Star, FileText, CreditCard, Users, Target, ClipboardList, Calendar,
  Music, Mic, Speaker, Radio, Headphones, Heart, Check, Shield, Zap,
  Award, TrendingUp, Clock, Mail, ArrowRight, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith('/api/uploads/')) return `${BACKEND_URL}${parsed.pathname}`;
  } catch (e) {}
  return url;
}

const iconMap = {
  Star, FileText, CreditCard, Users, Target, ClipboardList, Calendar,
  Music, Mic, Speaker, Radio, Headphones, Heart, Check, Shield, Zap,
  Award, TrendingUp, Clock, Mail
};

export default function PublicFeaturesPage() {
  const [settings, setSettings] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, featuresRes] = await Promise.all([
        axios.get(`${API}/frontend-settings/public`),
        axios.get(`${API}/frontend-settings/public/features`)
      ]);
      setSettings(settingsRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const seo = settings?.seo || {};
  const navLinks = [
    { label: 'Features', href: '/features' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-bold text-xl text-gray-900">
              {seo.site_title || 'DJ CRM'}
            </Link>
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(link => (
                <Link 
                  key={link.href}
                  to={link.href}
                  className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/auth">
                <Button variant="ghost" className="text-gray-700">Login</Button>
              </Link>
              <Link to="/free-trial">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-4">
            {navLinks.map(link => (
              <Link key={link.href} to={link.href} className="block text-gray-600" onClick={() => setMobileMenuOpen(false)}>
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-100 space-y-2">
              <Link to="/auth" className="block"><Button variant="outline" className="w-full">Login</Button></Link>
              <Link to="/free-trial" className="block"><Button className="w-full bg-blue-600 text-white">Start Free Trial</Button></Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Powerful Features for DJs
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to manage your DJ business in one place
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {features.length === 0 ? (
            <div className="text-center py-20">
              <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Features coming soon</p>
            </div>
          ) : (
            <div className="space-y-16">
              {features.map((feature, index) => {
                const IconComponent = iconMap[feature.icon] || Star;
                const isEven = index % 2 === 0;
                
                return (
                  <div 
                    key={feature.id || index}
                    className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                  >
                    <div className="flex-1 space-y-4">
                      <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                        <IconComponent className="w-7 h-7 text-blue-600" />
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {feature.title}
                      </h2>
                      <p className="text-lg text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                    {feature.screenshot_url && (
                      <div className="flex-1">
                        <img 
                          src={resolveImageUrl(feature.screenshot_url)} 
                          alt={feature.title}
                          onClick={() => setLightboxImage({ src: resolveImageUrl(feature.screenshot_url), alt: feature.title })}
                          className="rounded-xl shadow-xl border border-gray-200 w-full cursor-zoom-in hover:shadow-2xl transition-shadow"
                          data-testid={`feature-image-${index}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start your 14-day free trial today. No credit card required.
          </p>
          <Link to="/free-trial">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <span className="font-bold text-xl text-white">{seo.site_title || 'DJ CRM'}</span>
            <p className="mt-2 text-sm">{settings?.footer_text || '© 2024 DJ CRM. All rights reserved.'}</p>
          </div>
          <div className="flex items-center gap-6">
            {navLinks.map(link => (
              <Link key={link.href} to={link.href} className="text-gray-400 hover:text-white text-sm">{link.label}</Link>
            ))}
            <Link to="/auth" className="text-gray-400 hover:text-white text-sm">Login</Link>
          </div>
        </div>
      </footer>
      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
